// =====================================================
// COUNTERFACTUAL PROOF ENGINE - TYPE DEFINITIONS
// Track what would have happened if user traded when AI said NO_TRADE
// =====================================================

export type TrackingStatus = 'PENDING' | 'TRACKING' | 'COMPLETED' | 'FAILED';

export interface CounterfactualTracking {
  id: string;
  journalEntryId: string;
  userId: string;
  
  // Price at decision time
  priceAtDecision: number;
  directionIfTraded: 'LONG' | 'SHORT' | null;
  
  // Price tracking over time
  priceAfter1h: number | null;
  priceAfter4h: number | null;
  priceAfter24h: number | null;
  
  // Calculated metrics
  maxAdverseExcursion: number | null;
  maxFavorableExcursion: number | null;
  
  // Capital preservation calculations
  hypotheticalStopLoss: number | null;
  hypotheticalRiskAmount: number | null;
  capitalPreservedR: number | null;
  capitalPreservedUsd: number | null;
  
  // Tracking status
  trackingStatus: TrackingStatus;
  trackingStartedAt: string;
  trackingCompletedAt: string | null;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export interface CounterfactualTrackingRow {
  id: string;
  journal_entry_id: string;
  user_id: string;
  price_at_decision: number;
  direction_if_traded: string | null;
  price_after_1h: number | null;
  price_after_4h: number | null;
  price_after_24h: number | null;
  max_adverse_excursion: number | null;
  max_favorable_excursion: number | null;
  hypothetical_stop_loss: number | null;
  hypothetical_risk_amount: number | null;
  capital_preserved_r: number | null;
  capital_preserved_usd: number | null;
  tracking_status: string;
  tracking_started_at: string;
  tracking_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CounterfactualStats {
  totalTracked: number;
  completedTracking: number;
  totalCapitalPreservedR: number;
  totalCapitalPreservedUsd: number;
  avgCapitalPreservedR: number | null;
  avgMaxAdverseExcursion: number | null;
  decisionsThatSavedCapital: number;
  decisionsThatMissedOpportunity: number;
}

export interface CreateCounterfactualInput {
  journalEntryId: string;
  userId: string;
  priceAtDecision: number;
  directionIfTraded: 'LONG' | 'SHORT' | null;
  hypotheticalStopLoss: number | null;
  hypotheticalRiskAmount?: number;
}

// Convert database row to CounterfactualTracking
export function rowToCounterfactual(row: CounterfactualTrackingRow): CounterfactualTracking {
  return {
    id: row.id,
    journalEntryId: row.journal_entry_id,
    userId: row.user_id,
    priceAtDecision: row.price_at_decision,
    directionIfTraded: row.direction_if_traded as 'LONG' | 'SHORT' | null,
    priceAfter1h: row.price_after_1h,
    priceAfter4h: row.price_after_4h,
    priceAfter24h: row.price_after_24h,
    maxAdverseExcursion: row.max_adverse_excursion,
    maxFavorableExcursion: row.max_favorable_excursion,
    hypotheticalStopLoss: row.hypothetical_stop_loss,
    hypotheticalRiskAmount: row.hypothetical_risk_amount,
    capitalPreservedR: row.capital_preserved_r,
    capitalPreservedUsd: row.capital_preserved_usd,
    trackingStatus: row.tracking_status as TrackingStatus,
    trackingStartedAt: row.tracking_started_at,
    trackingCompletedAt: row.tracking_completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Format capital preserved for display
export function formatCapitalPreserved(r: number | null, usd: number | null): string {
  if (r === null && usd === null) return 'Tracking...';
  
  const parts: string[] = [];
  if (r !== null) {
    const sign = r >= 0 ? '+' : '';
    parts.push(`${sign}${r.toFixed(1)}R`);
  }
  if (usd !== null) {
    const sign = usd >= 0 ? '+' : '';
    parts.push(`${sign}$${Math.abs(usd).toLocaleString()}`);
  }
  
  return parts.join(' / ');
}

// Get color class based on capital preserved
export function getCapitalPreservedColor(r: number | null): string {
  if (r === null) return 'text-muted-foreground';
  if (r > 0) return 'text-green-500';
  if (r < 0) return 'text-amber-500'; // Missed opportunity (yellow, not red)
  return 'text-muted-foreground';
}

// Get label for counterfactual result
export function getCounterfactualLabel(r: number | null): string {
  if (r === null) return 'Analyzing...';
  if (r > 0) return 'Capital Preserved';
  if (r < 0) return 'Opportunity Missed';
  return 'Neutral';
}
